/**
 * Helpers for resolving a free-text search query (postcode, city or a
 * general location/address) into coordinates, and for resolving the
 * browser's geolocation into coordinates, using the Google Maps
 * JavaScript API loaded in public/index.html.
 */

import { googleMapsApiKey } from '../config';

export interface Coordinates {
  lat: number;
  lng: number;
}

/** Matches UK postcodes, e.g. "SW1A 1AA", "EC1A1BB", "M1 1AE". */
const UK_POSTCODE_REGEX = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

/** Returns true when the query looks like a UK postcode. */
export const isPostcode = (query: string): boolean =>
  UK_POSTCODE_REGEX.test(query.trim());

/**
 * Returns true when the query looks like a plain city/town name, i.e. it
 * contains only letters, spaces, apostrophes and hyphens (no digits or
 * commas, which would suggest a fuller address/landmark search).
 */
const CITY_NAME_REGEX = /^[a-zA-Z\s'-]+$/;

export const isCityName = (query: string): boolean =>
  CITY_NAME_REGEX.test(query.trim());

export interface PostcodeSuggestion {
  placeId: string;
  /** Full display text, e.g. "SW1A 1AA, London, UK" - for showing in a dropdown. */
  text: string;
  /** Just the postcode portion, e.g. "SW1A 1AA" - for passing to searchByPostcode. */
  postcode: string;
}

let googleMapsLoadedResolve: () => void;
let googleMapsLoadedReject: (reason?: unknown) => void;

/**
 * Upper bound on how long we wait for the Google Maps script to report that
 * it has loaded before giving up. Without this, a blocked/failed script
 * load (invalid key, ad-blocker, offline, CSP, etc.) that doesn't trigger
 * the <script> element's `onerror` would leave `googleMapsReady` pending
 * forever, hanging any caller awaiting it.
 */
const GOOGLE_MAPS_LOAD_TIMEOUT_MS = 10000;

export const googleMapsReady = new Promise<void>((resolve, reject) => {
  if (window.google?.maps) {
    resolve();
    return;
  }

  const timeoutId = setTimeout(() => {
    reject(new Error('Timed out waiting for the Google Maps script to load.'));
  }, GOOGLE_MAPS_LOAD_TIMEOUT_MS);

  googleMapsLoadedResolve = () => {
    clearTimeout(timeoutId);
    resolve();
  };
  googleMapsLoadedReject = (reason) => {
    clearTimeout(timeoutId);
    reject(reason);
  };
});

// Expose callback globally - invoked by the Google Maps script's `callback`
// query param once it has successfully loaded.
(window as any).onGoogleMapsLoad = () => {
  if (googleMapsLoadedResolve) {
    googleMapsLoadedResolve();
  }
};

// Invoked by the <script onerror="..."> handler in public/index.html if the
// Google Maps script request itself fails (e.g. blocked or offline).
(window as any).onGoogleMapsError = () => {
  if (googleMapsLoadedReject) {
    googleMapsLoadedReject(new Error('Failed to load the Google Maps script.'));
  }
};

const PLACES_AUTOCOMPLETE_URL =
  'https://places.googleapis.com/v1/places:autocomplete';

/** Extracts the UK postcode substring from a larger address string, if present. */
const extractPostcode = (text: string): string => {
  const match = text.match(/[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}/i);
  return match ? match[0] : text;
};

/**
 * Queries the Google Places API (New) Autocomplete (REST) endpoint for UK
 * postcode suggestions matching the given partial input. Restricts results
 * to the "postal_code" primary type and the "uk" region so overlapping
 * postcode formats from other countries aren't suggested.
 */
export const getPostcodeAutocomplete = async (
  input: string
): Promise<PostcodeSuggestion[]> => {
  if (!googleMapsApiKey) {
    console.error('Google Maps API key is not configured.');
    return [];
  }

  // Note: this calls the Places REST endpoint directly via fetch(), so it
  // doesn't depend on the window.google JS SDK/script being loaded.
  try {
    const response = await fetch(PLACES_AUTOCOMPLETE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': googleMapsApiKey,
        'X-Goog-FieldMask':
          'suggestions.placePrediction.placeId,' +
          'suggestions.placePrediction.text,' +
          'suggestions.placePrediction.structuredFormat.mainText',
      },
      body: JSON.stringify({
        input,
        includedPrimaryTypes: ['postal_code'],
        includedRegionCodes: ['gb'],
      }),
    });

    if (!response.ok) {
      console.error(
        'Postcode autocomplete request failed: ',
        await response.text()
      );
      return [];
    }

    const data = await response.json();
    const suggestions = data.suggestions || [];
    return suggestions
      .filter((suggestion: any) => suggestion.placePrediction)
      .map((suggestion: any) => {
        const prediction = suggestion.placePrediction;
        const text = prediction.text?.text ?? '';
        // mainText holds just the postcode for "postal_code" predictions;
        // fall back to extracting it from the full text if it's missing.
        const mainText = prediction.structuredFormat?.mainText?.text;
        const postcode = mainText ? mainText : extractPostcode(text);
        return { placeId: prediction.placeId, text, postcode };
      });
  } catch (error) {
    console.error('Postcode autocomplete request failed: ', error);
    return [];
  }
};

/** Resolves a free-text address/location into latitude & longitude. */
export const getCoordinatesFromAddress = async (
  address: string
): Promise<Coordinates | null> => {
  try {
    await googleMapsReady;
  } catch (error) {
    console.error('Google Maps JavaScript API failed to load: ', error);
    return null;
  }

  if (!window.google || !window.google.maps) {
    console.error('Google Maps JavaScript API is not loaded.');
    return null;
  }

  const geocoder = new window.google.maps.Geocoder();

  try {
    const response = await geocoder.geocode({ address });

    if (response.results.length > 0) {
      const location = response.results[0].geometry.location;
      return {
        lat: location.lat(),
        lng: location.lng(),
      };
    }
    return null;
  } catch (error) {
    console.error('Geocoding failed: ', error);
    return null;
  }
};

/** Resolves latitude & longitude into a human-readable address (reverse geocoding). */
export const getAddressFromCoordinates = async (
  coordinates: Coordinates
): Promise<string | null> => {
  try {
    await googleMapsReady;
  } catch (error) {
    console.error('Google Maps JavaScript API failed to load: ', error);
    return null;
  }

  if (!window.google || !window.google.maps) {
    console.error('Google Maps JavaScript API is not loaded.');
    return null;
  }

  const geocoder = new window.google.maps.Geocoder();

  try {
    const response = await geocoder.geocode({ location: coordinates });

    if (response.results.length > 0) {
      return response.results[0].formatted_address;
    }
    return null;
  } catch (error) {
    console.error('Reverse geocoding failed: ', error);
    return null;
  }
};

/**
 * Extracts { lat, lng } from the registration-service `/geocode` endpoint
 * response, which proxies a provider (e.g. Google Maps Geocoding API) and
 * may return coordinates as `latitude`/`longitude` or nested under a
 * `results[0].geometry.location` shape. Returns null if no usable
 * coordinates are found (e.g. the address could not be resolved).
 */
export const parseGeocodeResponse = (data: any): Coordinates | null => {
  if (!data) return null;

  const latitude = data.latitude ?? data.lat;
  const longitude = data.longitude ?? data.lng;
  if (typeof latitude === 'number' && typeof longitude === 'number') {
    return { lat: latitude, lng: longitude };
  }

  const location = data.results?.[0]?.geometry?.location;
  if (
    location &&
    typeof location.lat === 'number' &&
    typeof location.lng === 'number'
  ) {
    return { lat: location.lat, lng: location.lng };
  }

  return null;
};

/** Wraps the browser Geolocation API in a Promise. */
export const getCurrentPosition = (): Promise<Coordinates> =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser.'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      (error) => reject(error),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  });
